/**
 * The inventory's filters in the page address (T084), so opening a wine and coming back keeps
 * them. Anything unexpected in the address is ignored rather than failing.
 */
import type { TypeDeVin } from '../domain/cellar';
import {
  NO_FILTERS,
  type DueFilter,
  type InventoryFilters,
  type RatingFilterValue,
} from '../domain/filters';
import { isValidRating } from '../domain/rating';

const TYPES: TypeDeVin[] = ['rouge', 'blanc', 'rose', 'vendanges_tardives'];

export function filtersFromParams(params: URLSearchParams): InventoryFilters {
  const ratings: RatingFilterValue[] = (params.get('note') ?? '')
    .split(',')
    .filter((value) => value !== '')
    .flatMap((value): RatingFilterValue[] => {
      if (value === 'unrated') return ['unrated'];
      const n = Number(value);
      return isValidRating(n) ? [n] : [];
    });
  const alertParam = params.get('alert');
  const alert: DueFilter | undefined =
    alertParam === 'due'
      ? 'any'
      : alertParam === 'soon' || alertParam === 'past'
        ? alertParam
        : undefined;
  // Several values are repeated entries (?pays=FR&pays=IT); an older address with one of each
  // still reads the same.
  const pays = params
    .getAll('pays')
    .map((code) => code.trim())
    .filter((code) => code !== '');
  const regions = params
    .getAll('region')
    .map((region) => region.trim())
    .filter((region) => region !== '');
  const years = (key: string) =>
    params
      .getAll(key)
      .map(Number)
      .filter((year) => Number.isInteger(year) && year > 0);
  const annees = years('annee');

  return {
    ...NO_FILTERS,
    typesDeVin: params
      .getAll('type')
      .filter((type): type is TypeDeVin => (TYPES as string[]).includes(type)),
    domaines: params
      .getAll('domaine')
      .map((domaine) => domaine.trim())
      .filter((domaine) => domaine !== ''),
    drinkUntilYears: years('aboire'),
    annees,
    pays,
    regions,
    cepages: params
      .getAll('cepage')
      .map((variety) => variety.trim())
      .filter((variety) => variety !== ''),
    ratings,
    text: params.get('q') ?? '',
    includeEmpty: params.get('epuises') === '1',
    ...(alert ? { due: alert } : {}),
  };
}

export function filtersToParams(filters: InventoryFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const type of filters.typesDeVin) params.append('type', type);
  for (const domaine of filters.domaines) params.append('domaine', domaine);
  for (const year of filters.drinkUntilYears) params.append('aboire', String(year));
  for (const year of filters.annees) params.append('annee', String(year));
  for (const code of filters.pays) params.append('pays', code);
  for (const region of filters.regions) params.append('region', region);
  for (const variety of filters.cepages) params.append('cepage', variety);
  if (filters.ratings.length > 0) params.set('note', filters.ratings.join(','));
  if (filters.text !== '') params.set('q', filters.text);
  if (filters.includeEmpty) params.set('epuises', '1');
  if (filters.due !== undefined) params.set('alert', filters.due === 'any' ? 'due' : filters.due);
  return params;
}
